import { Injectable } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PAGE_PERFORMANCE_AUDIT_IMPACT } from '../../constants/page-performance-audit-impact';
import { PAGE_PERFORMANCE_CATEGORY } from '../../constants/page-performance-category';
import { PagePerformancePageDetailsService } from '../page-performance-page-details/page-performance-page-details.service';
import { PagePerformanceReportAudit, PagePerformanceReportCategoryType } from '@client/app/services/api/page-performance/page-performance-report/page-performance-report-repo.service';

interface AuditRef {
    id: string;
    weight?: number;
    relevantAudits?: string[];
    acronym?: string;
    group?: string;
}

@Injectable({
    providedIn: 'root',
})
export class AuditImpactService {
    constructor (
        private pageDetailsService: PagePerformancePageDetailsService,
        private translateService: TranslateService,
    ) {}

    getImpactValue (audit: PagePerformanceReportAudit): number {
        return this.getAuditImpactValue(audit);
    }

    getImpactClass (audit: PagePerformanceReportAudit): string {
        const impactValue = this.getAuditImpactValue(audit);
        switch (impactValue) {
            case PAGE_PERFORMANCE_AUDIT_IMPACT.LOW:
                return 'label-fixed label label-info';
            case PAGE_PERFORMANCE_AUDIT_IMPACT.MEDIUM:
                return 'label-fixed label label-warning';
            default:
                return 'label-fixed label label-danger';
        }
    }

    getImpactTranslation (audit: PagePerformanceReportAudit): string {
        const impactValue = this.getAuditImpactValue(audit);
        switch (impactValue) {
            case PAGE_PERFORMANCE_AUDIT_IMPACT.LOW:
                return this.translateService.getString('Low');
            case PAGE_PERFORMANCE_AUDIT_IMPACT.MEDIUM:
                return this.translateService.getString('Medium');
            case PAGE_PERFORMANCE_AUDIT_IMPACT.HIGH:
                return this.translateService.getString('High');
            default:
                return '';
        }
    }

    private getReport () {
        return this.pageDetailsService.getReport();
    }

    private getCategory (audit: PagePerformanceReportAudit): PagePerformanceReportCategoryType | undefined {
        const report = this.getReport();
        return Object.values(report?.categories || {}).find((category) => {
            return category.auditRefs?.some((ref) => ref.id === audit.id);
        });
    }

    private getAuditImpactValue (audit: PagePerformanceReportAudit): number {
        const category = this.getCategory(audit);
        if (!category) {
            return PAGE_PERFORMANCE_AUDIT_IMPACT.LOW;
        }

        switch (category.id) {
            case PAGE_PERFORMANCE_CATEGORY.PERFORMANCE:
                return this.getPerformanceImpactValue(audit);
            case PAGE_PERFORMANCE_CATEGORY.BEST_PRACTICE:
                return this.getBestPracticeImpactValue();
            case PAGE_PERFORMANCE_CATEGORY.SEO:
                return this.getSEOImpactValue();
            case PAGE_PERFORMANCE_CATEGORY.PWA:
                return this.getPWAImpactValue(category, audit);
            default:
                return PAGE_PERFORMANCE_AUDIT_IMPACT.LOW;
        }
    }

    private getBestPracticeImpactValue (): number {
        return PAGE_PERFORMANCE_AUDIT_IMPACT.MEDIUM;
    }

    private getPWAImpactValue (category: PagePerformanceReportCategoryType, audit: PagePerformanceReportAudit): number {
        const auditRef = category.auditRefs?.find((ref) => ref.id === audit.id) as AuditRef;
        const weight = auditRef?.weight ?? 0;
        if (weight <= 3) {
            return PAGE_PERFORMANCE_AUDIT_IMPACT.LOW;
        }
        if (weight <= 6) {
            return PAGE_PERFORMANCE_AUDIT_IMPACT.MEDIUM;
        }
        return PAGE_PERFORMANCE_AUDIT_IMPACT.HIGH;
    }

    private getSEOImpactValue (): number {
        return PAGE_PERFORMANCE_AUDIT_IMPACT.MEDIUM;
    }

    private getPerformanceImpactValue (audit: PagePerformanceReportAudit): number {
        const overallSavingsMs = audit.details?.overallSavingsMs;
        if (!overallSavingsMs || overallSavingsMs <= 50) {
            return PAGE_PERFORMANCE_AUDIT_IMPACT.LOW;
        }
        if (overallSavingsMs <= 250) {
            return PAGE_PERFORMANCE_AUDIT_IMPACT.MEDIUM;
        }
        return PAGE_PERFORMANCE_AUDIT_IMPACT.HIGH;
    }
}
